/**
 * Destination hero images — recommended full-width cover on detail pages.
 * Exact pixels are not enforced; any image file is accepted.
 */
export const TRIP_HERO_IMAGE_WIDTH = 1920;
export const TRIP_HERO_IMAGE_HEIGHT = 1080;
export const TRIP_HERO_IMAGE_SIZE_LABEL = `${TRIP_HERO_IMAGE_WIDTH} × ${TRIP_HERO_IMAGE_HEIGHT} px`;

/**
 * Key highlight images — recommended Open Graph / social-friendly card ratio.
 * Exact pixels are not enforced; any image file is accepted.
 */
export const TRIP_HIGHLIGHT_IMAGE_WIDTH = 1200;
export const TRIP_HIGHLIGHT_IMAGE_HEIGHT = 627;
export const TRIP_HIGHLIGHT_IMAGE_SIZE_LABEL =
    `${TRIP_HIGHLIGHT_IMAGE_WIDTH} × ${TRIP_HIGHLIGHT_IMAGE_HEIGHT} px`;

/** @deprecated Use TRIP_HERO_* or TRIP_HIGHLIGHT_* — kept for older imports. */
export const TRIP_DISPLAY_IMAGE_WIDTH = TRIP_HERO_IMAGE_WIDTH;
/** @deprecated Use TRIP_HERO_* or TRIP_HIGHLIGHT_* */
export const TRIP_DISPLAY_IMAGE_HEIGHT = TRIP_HERO_IMAGE_HEIGHT;
/** @deprecated Use TRIP_HERO_IMAGE_SIZE_LABEL or TRIP_HIGHLIGHT_IMAGE_SIZE_LABEL */
export const TRIP_DISPLAY_IMAGE_SIZE_LABEL = TRIP_HERO_IMAGE_SIZE_LABEL;

export const tripHeroImageHint =
    `Recommended size: ${TRIP_HERO_IMAGE_SIZE_LABEL} for best display on the destination page. Any image size is accepted.`;

export const tripHighlightImageHint =
    `Recommended size: ${TRIP_HIGHLIGHT_IMAGE_SIZE_LABEL} for best display on the destination page. Any image size is accepted.`;

/** @deprecated Use tripHeroImageHint or tripHighlightImageHint */
export const tripDisplayImageHint = tripHeroImageHint;

export const getImageDimensions = (file) =>
    new Promise((resolve, reject) => {
        if (!file) {
            reject(new Error('No file'));
            return;
        }
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            const width = img.naturalWidth || img.width;
            const height = img.naturalHeight || img.height;
            URL.revokeObjectURL(objectUrl);
            resolve({ width, height });
        };
        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            reject(new Error('Could not read image'));
        };
        img.src = objectUrl;
    });

/**
 * Accept any image file. Pixel size is recommended only — never blocked.
 */
const validateImageFile = async (file) => {
    if (!file) {
        return { ok: false, message: 'Please select an image file.' };
    }
    if (!file.type?.startsWith('image/')) {
        return {
            ok: false,
            message: 'Only image files are allowed (JPEG, PNG, GIF, WebP, etc.).',
        };
    }

    try {
        const { width, height } = await getImageDimensions(file);
        return { ok: true, width, height };
    } catch {
        // Still allow upload if dimensions cannot be read (some browsers / formats).
        return { ok: true };
    }
};

/**
 * Validates hero image is an image file (any pixel size accepted).
 */
export const validateTripHeroImage = (file) => validateImageFile(file);

/**
 * Validates highlight image is an image file (any pixel size accepted).
 */
export const validateTripHighlightImage = (file) => validateImageFile(file);

/** @deprecated Use validateTripHeroImage — kept for older hero upload call sites. */
export const validateTripDisplayImage = validateTripHeroImage;
